import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { addDays, addMonths, endOfMonth, format, isAfter, isBefore, isSameDay, isSameMonth, startOfDay, startOfMonth, startOfWeek } from 'date-fns';
type DateRangeCalendarProps = {
  start: Date | null;
  end: Date | null;
  onPick: (day: Date) => void;
};
const weekdays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

// Two months side by side on larger screens, one on phones
export function DateRangeCalendar({
  start,
  end,
  onPick
}: DateRangeCalendarProps) {
  const today = startOfDay(new Date());
  const [view, setView] = useState(() => startOfMonth(start ?? today));
  const [hover, setHover] = useState<Date | null>(null);
  const canGoBack = isAfter(view, startOfMonth(today));

  // While choosing the departure, preview the range under the cursor
  const previewEnd = start && !end && hover && isAfter(hover, start) ? hover : end;
  return <div className="px-5 pb-4 pt-4" data-id="element-307" data-mp="3f4exazekhf3z">
      <div className="relative grid grid-cols-1 gap-8 md:grid-cols-2" data-id="element-308" data-mp="1wk769myn3k6s">
        <button type="button" onClick={() => setView(v => addMonths(v, -1))} disabled={!canGoBack} aria-label="Previous month" className="absolute left-0 top-0 flex h-8 w-8 items-center justify-center transition-opacity duration-150 hover:opacity-60 disabled:opacity-20" data-id="element-309" data-mp="1sjdjiwwxobfh">
          <ChevronLeftIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-310" data-mp="1dtpd5h9wamkw" />
        </button>
        <button type="button" onClick={() => setView(v => addMonths(v, 1))} aria-label="Next month" className="absolute right-0 top-0 flex h-8 w-8 items-center justify-center transition-opacity duration-150 hover:opacity-60" data-id="element-311" data-mp="18vwb90fmkeqf">
          <ChevronRightIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-312" data-mp="v3znt9owit0d" />
        </button>

        {[0, 1].map(offset => {
        const month = addMonths(view, offset);
        const first = startOfWeek(startOfMonth(month), {
          weekStartsOn: 1
        });
        const last = endOfMonth(month);
        const days: Date[] = [];
        for (let d = first; !isAfter(d, last) || days.length % 7 !== 0; d = addDays(d, 1)) days.push(d);
        return <div key={offset} className={offset === 1 ? 'hidden md:block' : ''} data-id="element-313" data-mp="2zy8umgwm18bo">
              <p className="flex h-8 items-center justify-center text-[15px]" data-id="element-314" data-mp="33xhk39kmak1f">{format(month, 'MMMM yyyy')}</p>
              <div className="mt-3 grid grid-cols-7 text-center text-[12px] font-light text-ink/50" aria-hidden="true" data-id="element-315" data-mp="279lcgu75qz2c">
                {weekdays.map(w => <span key={w} className="py-1" data-id="element-316" data-mp="2g983cm29gmh5">
                    {w}
                  </span>)}
              </div>
              <div className="mt-1 grid grid-cols-7" role="grid" aria-label={format(month, 'MMMM yyyy')} data-id="element-317" data-mp="2l8uij11ngcsl">
                {days.map(day => {
              if (!isSameMonth(day, month)) return <span key={day.toISOString()} aria-hidden="true" data-id="element-318" data-mp="h11qbmly229" />;
              const past = isBefore(day, today);
              const isStart = !!start && isSameDay(day, start);
              const isEnd = !!previewEnd && isSameDay(day, previewEnd);
              const inRange = !!start && !!previewEnd && isAfter(day, start) && isBefore(day, previewEnd);
              const edge = isStart || isEnd;
              return <span key={day.toISOString()} className={`relative flex h-10 items-center justify-center ${inRange ? 'bg-bone' : ''}`} data-id="element-319" data-mp="3sxxftzrg75kx">
                      {/* Joins the range band to the first and last day */}
                      {isStart && start && previewEnd && !isSameDay(start, previewEnd) && <span className="absolute inset-y-0 left-1/2 right-0 bg-bone" aria-hidden="true" data-id="element-320" data-mp="3a2p7d1esm9b9" />}
                      {isEnd && start && !isStart && <span className="absolute inset-y-0 left-0 right-1/2 bg-bone" aria-hidden="true" data-id="element-321" data-mp="1899h45mmawgt" />}
                      <button type="button" disabled={past} onClick={() => onPick(day)} onMouseEnter={() => setHover(day)} onMouseLeave={() => setHover(null)} aria-pressed={edge} aria-label={format(day, 'EEEE d MMMM yyyy')} className={`relative flex h-10 w-10 items-center justify-center text-[14px] tabular-nums transition-colors duration-150 ${edge ? 'bg-ink font-medium text-paper' : past ? 'cursor-default font-light text-ink/25' : 'font-light text-ink hover:bg-ash'}`} data-id="element-322" data-mp="1fdr24qvhkeep">
                        {format(day, 'd')}
                        {isSameDay(day, today) && !edge && <span className="absolute bottom-1.5 h-[3px] w-[3px] rounded-full bg-ink" aria-hidden="true" data-id="element-323" data-mp="3v61bwok97hsa" />}
                      </button>
                    </span>;
            })}
              </div>
            </div>;
      })}
      </div>
    </div>;
}